import type { ReactNode } from 'react'
import { Check } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { useI18n } from '@/i18n'

export interface StepDef {
  id: string
  /** Short label (shown on wider screens; phones show "Step 2 of 5" + current label). */
  label: ReactNode
}

export interface StepperProps {
  steps: ReadonlyArray<StepDef>
  current: number
  className?: string
}

/** Progress for multi-step phone flows (find farmer → check → price → tag & weigh → slip). */
export function Stepper({ steps, current, className }: StepperProps) {
  const { t } = useI18n()
  const active = steps[current]
  return (
    <nav
      aria-label={t('common.step', { current: current + 1, total: steps.length })}
      className={className}
    >
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <span className="eyebrow">
          {t('common.step', { current: current + 1, total: steps.length })}
        </span>
        {active && (
          <span className="truncate text-[13px] font-semibold text-ink">{active.label}</span>
        )}
      </div>
      <ol className="flex items-center gap-1">
        {steps.map((s, i) => {
          const state = i < current ? 'done' : i === current ? 'current' : 'todo'
          return (
            <li
              key={s.id}
              className="flex flex-1 items-center"
              aria-current={state === 'current' ? 'step' : undefined}
            >
              <span className="sr-only">
                {s.label} {state === 'done' ? '✓' : ''}
              </span>
              <span
                aria-hidden="true"
                className={cn(
                  'h-1.5 w-full rounded-full transition-colors',
                  state === 'done' && 'bg-ok',
                  state === 'current' && 'bg-amber',
                  state === 'todo' && 'bg-rule',
                )}
              />
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

/** Vertical variant for the console or a summary screen. */
export function StepList({ steps, current }: StepperProps) {
  return (
    <ol className="flex flex-col gap-3">
      {steps.map((s, i) => {
        const done = i < current
        const now = i === current
        return (
          <li
            key={s.id}
            className="flex items-center gap-3"
            aria-current={now ? 'step' : undefined}
          >
            <span
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border font-mono text-[12px] font-bold',
                done && 'border-ok bg-ok text-white',
                now && 'border-amber bg-amber-wash text-amber-dark',
                !done && !now && 'border-rule text-muted',
              )}
            >
              {done ? <Check weight="bold" aria-hidden="true" /> : i + 1}
            </span>
            <span className={cn('text-[14px]', now ? 'font-semibold text-ink' : 'text-muted')}>
              {s.label}
            </span>
          </li>
        )
      })}
    </ol>
  )
}
